import { useState } from 'react';

import type { OrderCreate as OrderCreateBody } from '@solar/api-client';
import {
  APT_TYPE_LABEL,
  ErrorBox,
  INSTALL_TYPE_LABEL,
  Spinner,
  useAsync,
  won,
} from '@solar/ui';

import { api } from '../api';

/**
 * 주문 등록 — 레거시 WebService/order/detail.blade.php 등록 폼 파리티.
 *
 * 총사업비(price)·자부담(payment)은 서버가 상품가 기준으로 확정하므로 입력받지
 * 않는다 (레거시 OrderController@OrderAdd 512행과 동일). 등록 성공 시 주문
 * 상세로 이동해 서버가 계산한 금액을 확인한다.
 *
 * 코드값(apt_type/install_type/req_type)은 GET /api/v1/codes 에서 받고,
 * 한글 라벨은 @solar/ui 의 표시용 맵을 쓴다.
 */

const REQ_TYPE_LABEL: Record<string, string> = { HOST: '소유자', TENANT: '세입자' };

/** SH 임대(2)/재개발(7) — SH 보조금(price_sh)이 구 보조금 자리에 들어간다. */
const SH_TYPES = [2, 7];
const GUARD_ROOM = 6;

interface FormState {
  user_name: string;
  user_phone: string;
  user_birth: string;
  user_home: string;
  user_email: string;
  req_type: '' | 'host' | 'tenant';
  host: string;
  host_relation: string;
  host_install_agree: boolean;
  new_addr: string;
  addr_full: string;
  post_code: string;
  addr1: string;
  addr2: string;
  addr3: string;
  addr4: string;
  addr5: string;
  building_name: string;
  apt_type: number;
  com_year: string;
  company_id: string;
  def_product_id: string;
  install_date: string;
  install_type: '' | 'hook' | 'attachment' | 'slope_hook';
  price_si: string;
  price_borough: string;
  price_sh: string;
  payment_etc: string;
  payment_type: string;
  payment_check: 'Y' | 'N';
  etc: string;
}

const INITIAL: FormState = {
  user_name: '',
  user_phone: '',
  user_birth: '',
  user_home: '',
  user_email: '',
  req_type: '',
  host: '',
  host_relation: '',
  host_install_agree: false,
  new_addr: '',
  addr_full: '',
  post_code: '',
  addr1: '',
  addr2: '',
  addr3: '',
  addr4: '',
  addr5: '',
  building_name: '',
  apt_type: 1,
  com_year: '',
  company_id: '',
  def_product_id: '',
  install_date: '',
  install_type: '',
  price_si: '',
  price_borough: '',
  price_sh: '',
  payment_etc: '',
  payment_type: '',
  payment_check: 'N',
  etc: '',
};

function toInt(value: string): number | undefined {
  const n = Number(value);
  return value.trim() !== '' && Number.isFinite(n) ? Math.trunc(n) : undefined;
}

export function OrderCreate({
  role,
  companyId,
  onCreated,
  onCancel,
}: {
  role: number;
  /** 로그인 계정의 소속 회사 — 보급업체 계정의 팀/상품 조회에 쓴다. */
  companyId: number | null;
  onCreated: (orderId: number) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const codes = useAsync(() => api.codes(), []);

  // 에너지공사(1/2)만 회사를 지정한다. 보급업체는 서버가 자기 회사로 강제.
  const isEnergy = [1, 2].includes(role);
  const isSh = SH_TYPES.includes(form.apt_type);
  const isGuardRoom = form.apt_type === GUARD_ROOM;

  // 회사 셀렉트 (에너지공사 전용) — GET /companies. 업체 계정에게는 어차피
  // 자기 회사만 내려오므로 셀렉트 자체를 보여주지 않는다.
  const companies = useAsync(
    () => (isEnergy ? api.listCompanies({ size: 200 }) : Promise.resolve(null)),
    [isEnergy],
  );

  // 주문을 등록할 회사 — 팀/상품 목록의 기준이 된다.
  const effectiveCompanyId = isEnergy ? toInt(form.company_id) : (companyId ?? undefined);

  // 상품 셀렉트 — GET /define/products. 목록은 서버가 회사 격리를 적용하고,
  // 에너지공사 계정은 전체가 내려오므로 선택한 회사 것(+공용)만 남긴다.
  const products = useAsync(() => api.listProducts({ size: 200 }), []);
  const productOptions = (products.data?.items ?? []).filter(
    (p) =>
      !isEnergy ||
      p.build_company_id === null ||
      (effectiveCompanyId !== undefined && p.build_company_id === effectiveCompanyId),
  );

  // 기사 배정 (선택) — 레거시 순서대로 팀을 먼저 고르고 그 팀의 기사를 고른다.
  // 팀 없는 기사도 배정할 수 있다('팀 미지정' 선택지 — 레거시 예외 유지).
  const [teamSel, setTeamSel] = useState<'' | 'none' | number>('');
  const [engineerId, setEngineerId] = useState<number | ''>('');
  const teams = useAsync(
    () =>
      effectiveCompanyId === undefined
        ? Promise.resolve(null)
        : api.listTeams(effectiveCompanyId),
    [effectiveCompanyId],
  );
  const engineers = useAsync(() => api.listUsers({ role: 4, size: 200 }), []);
  const engineerOptions = (engineers.data?.items ?? []).filter((u) => {
    if (effectiveCompanyId !== undefined && u.build_company_id !== effectiveCompanyId) return false;
    if (teamSel === '') return false;
    if (teamSel === 'none') return u.build_team_id === null;
    return u.build_team_id === teamSel;
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): string | null {
    if (!form.user_name.trim()) return '이름을 입력해 주세요.';
    if (!form.user_phone.trim()) return '휴대전화를 입력해 주세요.';
    if (!form.new_addr.trim()) return '도로명 주소를 입력해 주세요.';
    if (toInt(form.def_product_id) === undefined) return '상품을 선택해 주세요.';
    if (isEnergy && toInt(form.company_id) === undefined)
      return '에너지공사 계정은 등록할 회사를 선택해야 합니다.';
    return null;
  }

  async function submit() {
    const invalid = validate();
    if (invalid !== null) {
      setError(invalid);
      return;
    }

    const body: OrderCreateBody = {
      user_name: form.user_name.trim(),
      user_phone: form.user_phone.trim(),
      new_addr: form.new_addr.trim(),
      def_product_id: toInt(form.def_product_id) as number,
      company_id: isEnergy ? toInt(form.company_id) : undefined,
      user_birth: form.user_birth.trim() || undefined,
      user_home: form.user_home.trim() || undefined,
      user_email: form.user_email.trim() || undefined,
      addr_full: form.addr_full.trim() || undefined,
      post_code: form.post_code.trim() || undefined,
      addr1: form.addr1.trim() || undefined,
      addr2: form.addr2.trim() || undefined,
      addr3: form.addr3.trim() || undefined,
      addr4: form.addr4.trim() || undefined,
      addr5: form.addr5.trim() || undefined,
      building_name: form.building_name.trim() || undefined,
      apt_type: form.apt_type,
      com_year: form.com_year.trim() || undefined,
      host: form.host.trim() || undefined,
      host_relation: form.host_relation.trim() || undefined,
      host_install_agree: form.host_install_agree ? 'Y' : 'N',
      req_type: form.req_type || undefined,
      install_date: form.install_date || undefined,
      install_type: form.install_type || undefined,
      price_si: toInt(form.price_si) ?? 0,
      price_borough: isSh ? 0 : (toInt(form.price_borough) ?? 0),
      price_sh: isSh ? (toInt(form.price_sh) ?? 0) : 0,
      payment_etc: toInt(form.payment_etc),
      payment_type: form.payment_type.trim() || undefined,
      payment_check: form.payment_check,
      engineer_id: engineerId === '' ? undefined : engineerId,
      // '팀 미지정' 기사는 team_id 없이 배정한다 (레거시 예외 유지).
      // 기사를 고르지 않았으면 팀도 보내지 않는다.
      team_id: engineerId !== '' && typeof teamSel === 'number' ? teamSel : undefined,
      etc: form.etc.trim() || undefined,
    };

    setBusy(true);
    setError(null);
    try {
      const created = await api.createOrder(body);
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : '주문을 등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail create-form">
      <div className="detail__head">
        <h2>주문 등록</h2>
        <button type="button" className="link" onClick={onCancel}>
          목록으로
        </button>
      </div>

      {codes.error && <ErrorBox message={codes.error} onRetry={codes.reload} />}

      <h3 className="section">고객 정보</h3>
      <section className="panel form-grid">
        <label className="f">
          이름 *
          <input value={form.user_name} onChange={(e) => set('user_name', e.target.value)} />
        </label>
        <label className="f">
          휴대전화 *
          <input
            value={form.user_phone}
            onChange={(e) => set('user_phone', e.target.value)}
            placeholder="01012345678"
          />
        </label>
        <label className="f">
          생년월일
          <input
            value={form.user_birth}
            onChange={(e) => set('user_birth', e.target.value)}
            placeholder="YYYYMMDD"
            disabled={isGuardRoom}
          />
        </label>
        <label className="f">
          전화번호
          <input value={form.user_home} onChange={(e) => set('user_home', e.target.value)} />
        </label>
        <label className="f">
          이메일
          <input value={form.user_email} onChange={(e) => set('user_email', e.target.value)} />
        </label>
        <label className="f">
          신청인 구분
          <select
            value={form.req_type}
            onChange={(e) => set('req_type', e.target.value as FormState['req_type'])}
          >
            <option value="">선택 안 함</option>
            {(codes.data?.req_type ?? []).map((c) => (
              <option key={String(c.value)} value={String(c.value)}>
                {REQ_TYPE_LABEL[c.name] ?? c.name}
              </option>
            ))}
          </select>
        </label>
        {form.req_type === 'tenant' && (
          <>
            <label className="f">
              소유자 이름
              <input value={form.host} onChange={(e) => set('host', e.target.value)} />
            </label>
            <label className="f">
              소유자와의 관계
              <input
                value={form.host_relation}
                onChange={(e) => set('host_relation', e.target.value)}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={form.host_install_agree}
                onChange={(e) => set('host_install_agree', e.target.checked)}
              />
              소유자 설치 동의
            </label>
          </>
        )}
      </section>

      <h3 className="section">주소</h3>
      <section className="panel form-grid">
        <label className="f wide">
          도로명 주소 *
          <input value={form.new_addr} onChange={(e) => set('new_addr', e.target.value)} />
        </label>
        <label className="f wide">
          지번 주소
          <input value={form.addr_full} onChange={(e) => set('addr_full', e.target.value)} />
        </label>
        <label className="f">
          우편번호
          <input value={form.post_code} onChange={(e) => set('post_code', e.target.value)} />
        </label>
        <label className="f">
          시/도
          <input value={form.addr1} onChange={(e) => set('addr1', e.target.value)} />
        </label>
        <label className="f">
          구/군
          <input value={form.addr2} onChange={(e) => set('addr2', e.target.value)} />
        </label>
        <label className="f">
          동/읍/면
          <input value={form.addr3} onChange={(e) => set('addr3', e.target.value)} />
        </label>
        <label className="f">
          건물명
          <input
            value={form.building_name}
            onChange={(e) => set('building_name', e.target.value)}
          />
        </label>
        <label className="f">
          동 (상세)
          <input value={form.addr4} onChange={(e) => set('addr4', e.target.value)} />
        </label>
        <label className="f">
          호 (상세)
          <input value={form.addr5} onChange={(e) => set('addr5', e.target.value)} />
        </label>
      </section>

      <h3 className="section">건물 / 설치</h3>
      <section className="panel form-grid">
        <label className="f">
          건물 유형
          <select
            value={form.apt_type}
            onChange={(e) => set('apt_type', Number(e.target.value))}
          >
            {(codes.data?.apt_type ?? []).map((c) => (
              <option key={String(c.value)} value={Number(c.value)}>
                {APT_TYPE_LABEL[Number(c.value)] ?? c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          준공년도
          <input value={form.com_year} onChange={(e) => set('com_year', e.target.value)} />
        </label>
        <label className="f">
          설치 방식
          <select
            value={form.install_type}
            onChange={(e) => set('install_type', e.target.value as FormState['install_type'])}
          >
            <option value="">선택 안 함</option>
            {(codes.data?.install_type ?? []).map((c) => (
              <option key={String(c.value)} value={String(c.value)}>
                {INSTALL_TYPE_LABEL[String(c.value)] ?? c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          설치 희망일
          <input
            type="date"
            value={form.install_date}
            onChange={(e) => set('install_date', e.target.value)}
          />
        </label>
      </section>

      <h3 className="section">상품 / 회사</h3>
      <section className="panel form-grid">
        {isEnergy && (
          <label className="f">
            회사 *
            {companies.error ? (
              <span className="error">{companies.error}</span>
            ) : (
              <select
                value={form.company_id}
                onChange={(e) => {
                  // 회사가 바뀌면 그 회사 기준의 상품/팀/기사 선택을 초기화한다.
                  set('company_id', e.target.value);
                  set('def_product_id', '');
                  setTeamSel('');
                  setEngineerId('');
                }}
              >
                <option value="">선택</option>
                {(companies.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}
        <label className="f">
          상품 *
          {products.error ? (
            <span className="error">{products.error}</span>
          ) : (
            <select
              value={form.def_product_id}
              onChange={(e) => set('def_product_id', e.target.value)}
              disabled={isEnergy && effectiveCompanyId === undefined}
            >
              <option value="">
                {isEnergy && effectiveCompanyId === undefined ? '회사를 먼저 선택하세요' : '선택'}
              </option>
              {productOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {won(p.price)}
                </option>
              ))}
            </select>
          )}
        </label>
        <p className="muted wide">
          상품이 해당 회사의 것이 아니면 서버가 등록을 거부합니다. 총사업비는 상품가 기준으로
          서버가 확정합니다.
        </p>
      </section>

      <h3 className="section">기사 배정 (선택)</h3>
      <section className="panel">
        {teams.error && <ErrorBox message={teams.error} onRetry={teams.reload} />}
        {engineers.error && <ErrorBox message={engineers.error} onRetry={engineers.reload} />}
        {engineers.loading && <Spinner label="기사 목록 불러오는 중…" />}

        {effectiveCompanyId === undefined ? (
          <p className="muted">회사를 먼저 선택하면 팀 목록이 나타납니다.</p>
        ) : (
          <>
            <select
              value={teamSel}
              onChange={(e) => {
                const v = e.target.value;
                setTeamSel(v === '' || v === 'none' ? (v as '' | 'none') : Number(v));
                setEngineerId('');
              }}
            >
              <option value="">배정 안 함</option>
              <option value="none">팀 미지정 기사</option>
              {(teams.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>

            {teamSel !== '' && (
              <select
                value={engineerId}
                onChange={(e) => setEngineerId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">기사 선택 ({engineerOptions.length}명)</option>
                {engineerOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name ?? u.user_id}
                  </option>
                ))}
              </select>
            )}

            {engineerId !== '' && (
              <p className="muted">기사를 배정하면 주문이 기사배정(2) 상태로 등록됩니다.</p>
            )}
          </>
        )}
      </section>

      <h3 className="section">금액 / 보조금</h3>
      <section className="panel form-grid">
        <label className="f">
          시 보조금
          <input
            type="number"
            min={0}
            value={form.price_si}
            onChange={(e) => set('price_si', e.target.value)}
          />
        </label>
        {isSh ? (
          <label className="f">
            SH 보조금
            <input
              type="number"
              min={0}
              value={form.price_sh}
              onChange={(e) => set('price_sh', e.target.value)}
            />
          </label>
        ) : (
          <label className="f">
            구 보조금
            <input
              type="number"
              min={0}
              value={form.price_borough}
              onChange={(e) => set('price_borough', e.target.value)}
              disabled={isGuardRoom}
            />
          </label>
        )}
        <label className="f">
          기타 비용
          <input
            type="number"
            min={0}
            value={form.payment_etc}
            onChange={(e) => set('payment_etc', e.target.value)}
          />
        </label>
        <label className="f">
          결제 방식
          <input
            value={form.payment_type}
            onChange={(e) => set('payment_type', e.target.value)}
            placeholder="예: 현금, 카드"
          />
        </label>
        <label className="f">
          수납 여부
          <select
            value={form.payment_check}
            onChange={(e) => set('payment_check', e.target.value as 'Y' | 'N')}
            disabled={isGuardRoom || isSh}
          >
            <option value="N">미수납</option>
            <option value="Y">수납 완료</option>
          </select>
        </label>
        <p className="muted wide">
          총사업비와 자부담금은 서버가 상품가 기준으로 확정합니다
          {isSh && ' (SH 유형은 수납 완료로 자동 처리)'}
          {isGuardRoom && ' (경비실은 자부담 0원 · 수납 완료로 자동 처리)'}. 등록 후 상세
          화면에서 계산 결과를 확인하세요.
        </p>
      </section>

      <h3 className="section">기타</h3>
      <section className="panel">
        <textarea
          rows={3}
          value={form.etc}
          onChange={(e) => set('etc', e.target.value)}
          placeholder="메모"
        />
      </section>

      {error && <p className="error">{error}</p>}

      <div className="row form-actions">
        <button type="button" onClick={onCancel}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '주문 등록'}
        </button>
      </div>
    </div>
  );
}
